// The model-predictive instructor: WT's actual control loop, on our own FDM.
//
// The July rework took War Thunder's CONTRACT — a world-anchored desired
// direction of flight — without taking its SOLVER, and flew the order with a
// damped pursuit instead. The pursuit is honest but blind: its gains are one
// fleet-wide compromise, so the heavies' fine endgame crawled (Mustang
// 8.3 s to settle a gun solution; B-17 and Marquee never under the
// 0.8-degree bar), the jets parked a ~1.3-degree standing miss (a
// proportional endgame needs standing error to produce output, so it settles
// ABOVE zero), and a rim-pinned turn rode whatever bank geometry picked
// instead of the bank the G ceiling can actually use. Those are the four
// residuals docs/mouse-aim-predictive-instructor.md tabulates, and this file
// is the documented next step it hands off: the simulate-and-score loop
// parameterized in docs/reference/war-thunder/gameplay.blkx › mouseAim
// (numSteps, predictionTime, reducedFlightModelLoop) and the patent's
// embodiments 303B/C.
//
// The design, faithful to the reference:
//   - Per input tick, roll the REAL client FDM (FDM.step on a copied state —
//     WT's "reducedFlightModelLoop" is literally our own integrator at coarse
//     dt) forward ~0.7 s for a small set of candidate elevator positions,
//     centered on last tick's answer (WT's elevRuddPosCaching).
//   - Score each terminal state: alignment with the wish direction MINUS a
//     penalty for residual rotation — arriving STOPPED is the whole point
//     (preciseRotStopDetection). The stop penalty scales up as the nose
//     closes, so a big pull is never taxed for carrying honest pitch rate.
//   - The AoA/G limits live INSIDE the rollout (maxAoaFactorMult /
//     overloadMult): a candidate that busts them pays per step, so the search
//     never proposes what a limiter would veto — max-performance pulls
//     without the recoil-after-the-overshoot hump the old taper had, and no
//     jetLead anticipation term, because anticipation is what prediction IS.
//   - Rudder is the same search on the lateral axis with the elevator frozen
//     at its winner — the fine gun-laying finisher, now sized by each
//     airframe's own fin because the rollout steps that airframe's inertia.
//   - Roll is NOT searched, exactly as in WT ("rollPidKpMult" is a plain PID
//     there): the shipped pursuit/carve roll loop keeps the wings. The one
//     roll change under this instructor is the bank governor below.
//
// The bank governor: the pursuit picks a rim-turn's bank by GEOMETRY (roll
// until the target sits in the lift plane, ~88-90 deg) and then the vertical
// axis can't hold it — a level turn at 88 deg wants 29 g — so the nose sags
// and the wings see-saw (Skywriter: sigma 29 deg of bank). The optimal bank
// is what the plane's own limits can use: n_sustained = min(structural G,
// what the wing lifts at this speed), bank* = acos(1/n). The solver already
// carries both numbers for its rollout clamps, so the governor simply leans
// on the roll command once the bank is PAST bank* — under it, the pursuit
// and carve fly exactly as shipped, so fine work and ordinary turns are
// untouched by construction.
//
// Everything here is a pure function of (cfg, state, wish, limits) + the
// cached previous answer — plain FDM-kit math, no THREE, no clocks, so the
// headless probes drive this exact code (mouse_feel_probe's `pred` driver
// runs it side by side with the shipped pursuit).

import * as FDM from "./fdm.js";

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// The rollout: WT's nominal 0.7 s of predictionTime, at twice WT's numSteps.
// The step size is load-bearing, not a budget knob: at 140 ms steps the
// coarse model misrepresents jet rotational dynamics enough that the
// solve-every-tick loop rang under 150 ms of delay (the laggy fine-track
// fence read 6.6 deg RMS, bank swinging 34) — at 70 ms the same fence reads
// BETTER than the pursuit. Ten steps is still ~a quarter millisecond per
// solve in V8.
const PRED_T = 0.7;
const PRED_STEPS = 10;
// Candidate ladders, centered on the cached previous answer. Coarse rungs
// find the regime (full pull vs unload); a shrinking-triplet refine then
// walks to the answer in ~0.01 steps — the first cut used fixed refine rungs
// and the fine-track probe caught the quantization as a standing 0.7-degree
// dither, the exact sin this solver exists to kill. ~11 rollouts per axis
// per tick, ~110 FDM steps, still well under a millisecond.
const ELEV_COARSE = [-0.6, -0.25, 0, 0.25, 0.6];
const RUDD_COARSE = [-0.24, -0.1, 0, 0.1, 0.24];
const REFINE_H = [0.08, 0.027, 0.009]; // triplet half-widths, shrinking
const ELEV_MIN = -0.7; // the shipped push clamp: you can always unload, never tuck
const ELEV_MAX = 1;
const RUDD_MAX = 0.4; // the shipped rudder clamp
// Rollout limits (WT: maxAoaFactorMult ~0.95, overloadMult 0.85-0.92): a
// candidate pays per step it spends beyond them. AoA is priced steeply — the
// buffet is a cliff — G more gently, so the search rides the ceiling instead
// of flinching under it.
const AOA_FRAC = 0.95;
const G_FRAC = 0.92;
const PEN_AOA = 40; // score per rad-step of AoA past the clamp
const PEN_G = 0.35; // score per g-step past the clamp
// The stop discipline (WT's preciseRotStopDetection), priced with geometry
// instead of a weight: after the rollout, the terminal attitude COASTS for
// T_COAST seconds on its residual rotation — no forces, just qIntegrate —
// and the score is the coasted miss. Rotation that is still closing on the
// aim improves the coasted miss and is rewarded; rotation that would carry
// the nose past the aim worsens it and is taxed, in exact proportion to the
// overshoot it would cause. The first cut taxed |omega| with a hand-tuned
// weight and the probe caught both failure modes at once: near the nose the
// tax dwarfed the quadratic dot() gradient and parked a 0.7-degree standing
// miss (the exact sin this solver exists to kill), and mid-flick it
// throttled an honest pull that the coast now correctly reads as "still
// arriving".
const T_COAST = 0.3;
// Bank governor gains: autopilotLevel's delay-proof capture pair — this loop
// rides the same laggy path (30 Hz resample, prediction, slow frames) and
// those gains are the ones proven to stay settled past ~130 ms of it.
const GOV_P = 2.0;
const GOV_D = 0.5;

/**
 * The sustained-turn ceiling, and the bank that spends it exactly. n is the
 * lesser of the structural ceiling and what the wing can lift at this speed
 * and height (the same numbers the rollout clamps to); bank* = acos(1/n) is
 * the steepest bank a level turn can carry at that load. Below ~1.15 g of
 * headroom there is no turn to govern and the governor stands down.
 */
export function optimalBank(cfg, state, limits) {
  const speed = FDM.vLen(state.vel);
  if (speed < 30) return null;
  const rho = FDM.airDensity(state.pos.y);
  const qS = 0.5 * rho * speed * speed * cfg.wingAreaM2;
  const clUsable = clamp(cfg.cl0 + cfg.clAlpha * cfg.alphaStallRad * AOA_FRAC, 0, cfg.clMax);
  const nAero = (qS * clUsable) / (cfg.massKg * FDM.GRAVITY);
  const n = Math.min((limits?.gMax ?? 6) * G_FRAC, nAero);
  if (n < 1.15) return null;
  return Math.acos(1 / n);
}

function rollout(cfg, state, elev, rudd, roll, throttle, limits, dir, prevE, prevR) {
  const sim = {
    pos: { ...state.pos },
    vel: { ...state.vel },
    att: { ...state.att },
    omega: { ...state.omega },
  };
  // The command in flight: the live client's command travels the laggy path
  // autopilotLevel documents (~130-150 ms of resample + prediction + slow
  // frames), so for the first rollout step the plane is still flying LAST
  // tick's answer, not this candidate. Half the delay armor (the fine step
  // size above is the other half): both were needed before the jets — fast
  // airframes, hot loop gain — stopped ringing at 150 ms in the laggy
  // fine-track fence.
  const held = { pitch: prevE, roll, yaw: prevR, throttle };
  const ctl = { pitch: elev, roll, yaw: rudd, throttle };
  const dt = PRED_T / PRED_STEPS;
  const aMax = (limits?.alphaStallRad ?? 0.3) * AOA_FRAC;
  const gMax = (limits?.gMax ?? 6) * G_FRAC;
  let pen = 0;
  for (let i = 0; i < PRED_STEPS; i++) {
    FDM.step(cfg, sim, i === 0 ? held : ctl, dt);
    pen += Math.max(0, Math.abs(sim.alpha ?? 0) - aMax) * PEN_AOA;
    pen += Math.max(0, (sim.nz ?? 1) - gMax) * PEN_G;
  }
  // The coast (see T_COAST): where the residual rotation would leave the
  // nose. The miss is measured THERE, so arriving stopped is priced in
  // geometry, not in a tuned weight.
  const attC = FDM.qIntegrate(sim.att, sim.omega, T_COAST);
  const db = FDM.qRotate(FDM.qConj(attC), dir);
  return { err: Math.acos(clamp(db.x, -1, 1)), lat: Math.abs(db.z), pen };
}

// One-dimensional search: coarse ladder around the cached previous answer,
// then a shrinking-triplet descent (evaluate best±h, keep the max, halve-ish
// h). Continuous enough that consecutive ticks don't dither between rungs.
function search1D(score, prev, coarse, lo, hi) {
  let best = clamp(prev, lo, hi);
  let bestScore = score(best);
  for (const d of coarse) {
    if (d === 0) continue;
    const c = clamp(prev + d, lo, hi);
    const sc = score(c);
    if (sc > bestScore) {
      bestScore = sc;
      best = c;
    }
  }
  for (const h of REFINE_H) {
    for (const s of [-1, 1]) {
      const c = clamp(best + s * h, lo, hi);
      const sc = score(c);
      if (sc > bestScore) {
        bestScore = sc;
        best = c;
      }
    }
  }
  return best;
}

export class Instructor {
  constructor() {
    this.prevE = 0; // last tick's elevator answer (WT's elevRuddPosCaching)
    this.prevR = 0; // last tick's rudder answer
    this.govW = 0; // the bank governor's patience (0..1, earned over a held over-bank)
  }

  /**
   * Solve elevator + rudder for the wish direction. `roll` is the roll
   * command the shipped roll loop is about to fly this tick — the rollout
   * carries it so the prediction banks the way the plane actually will.
   * Returns { pitch, yaw }.
   */
  solve(cfg, state, dir, limits, roll = 0, throttle = 0.7) {
    const db0 = FDM.qRotate(FDM.qConj(state.att), dir);

    // Elevator: scored on the coasted miss and the rollout's limit tax.
    const scoreE = (e) => {
      const r = rollout(cfg, state, e, this.prevR, roll, throttle, limits, dir, this.prevE, this.prevR);
      return -r.err - r.pen;
    };
    let bestE = search1D(scoreE, this.prevE, ELEV_COARSE, ELEV_MIN, ELEV_MAX);

    // Target behind the wing line: no 0.7 s rollout can tell the candidates
    // apart out there, so keep the shipped haul-around floor — the one order
    // that is always right with the target aft is "pull".
    if (db0.x < 0) bestE = Math.max(bestE, 0.7);

    // Rudder: same search on the coasted LATERAL miss, elevator frozen at
    // its winner. This is what kills the standing miss: the search finds the
    // rudder that arrives at zero and stays there, per airframe, because the
    // rollout steps this airframe's own fin and inertia.
    const scoreR = (rd) => {
      const r = rollout(cfg, state, bestE, rd, roll, throttle, limits, dir, this.prevE, this.prevR);
      return -r.lat - r.pen;
    };
    const bestR = search1D(scoreR, this.prevR, RUDD_COARSE, -RUDD_MAX, RUDD_MAX);

    this.prevE = bestE;
    this.prevR = bestR;
    return { pitch: bestE, yaw: bestR };
  }

  /**
   * The bank governor (see the header). Returns the roll command to fly —
   * the shipped command, blended toward a capture onto the optimal bank as
   * the governor's patience fills. Three disciplines, each probe-taught:
   *   - It REPLACES rather than adds. The first cut added a correction on
   *     top of the pursuit's saturated roll and simply lost the arm-wrestle
   *     — the Spitfire settled 13 degrees PAST the vertical at half the
   *     turn rate, the exact two-controllers-one-surface sin the handoff
   *     doc warns about.
   *   - It is PATIENT: the blend weight is earned over ~half a second of
   *     HELD over-bank and decays fast. A flick's transient overshoot
   *     through 100 degrees is free (flick-stop peak bank is a fenced cell);
   *     only the rim-turn regime — over-bank held tick after tick — gets
   *     leaned on.
   *   - It only governs TURNS: the gate fades out as the aim leaves the
   *     horizon band, same |dir.y| ramp as the carve's vert gate, so a
   *     roll-through toward a high aim (the corkscrew entry, the loop) is
   *     never captured mid-roll.
   */
  governRoll(cfg, state, dir, limits, roll, dt) {
    const bankOpt = optimalBank(cfg, state, limits);
    if (bankOpt === null) return roll;
    const up = FDM.qRotate(state.att, FDM.v3(0, 1, 0));
    const right = FDM.qRotate(state.att, FDM.v3(0, 0, 1));
    const bank = Math.atan2(-right.y, up.y);
    const over = Math.abs(bank) - bankOpt;
    // The stunt release is ONE-SIDED: an aim carried well ABOVE the horizon
    // (a loop entry, a rolling climb) frees the wings; an aim BELOW does
    // not. The first cut released on |dir.y| both ways — the carve's vert
    // ramp — and it was self-defeating: in the exact spiral this governor
    // exists to stop, the rim drags the aim below the horizon as the camera
    // banks over, so the deeper the spiral the freer the wings. (The
    // split-S needs no release here: past 120 degrees of bank the upright
    // fade below zeroes the governor, same fence as the carve's.)
    const high = 1 - clamp((dir.y - 0.5) / 0.2, 0, 1);
    // Hysteresis on the engage line: once governing, keep governing until
    // the bank is comfortably back under the optimum. Symmetric fast decay
    // limit-cycled — the pursuit re-overbanked during every decay window and
    // the "governed" turn breathed 20 degrees of bank at a 1 Hz wallow.
    const engaged = over > (this.govW > 0.5 ? -0.09 : 0) && high > 0;
    this.govW = clamp(this.govW + (engaged ? dt / 0.5 : -dt / 0.6), 0, 1);
    if (this.govW <= 0) return roll;
    const capture = clamp(
      (Math.sign(bank) * bankOpt - bank) * GOV_P - state.omega.x * GOV_D,
      -1, 1,
    );
    const upright = clamp(1 + up.y / 0.5, 0, 1); // gone by 120 deg of bank
    // Near-vertical stand-down, on a much steeper band than the carve's: a
    // split-S pull sweeps its bank reading through the governor's capture
    // zone with the nose 60-90 degrees down, and must not be "rescued" into
    // a spiral mid-loop. The band starts at |fwd.y| 0.8 (~53 deg) — far
    // steeper than any spiral's nose (the governed spiral rides ~30-40 deg
    // down at its worst) — because the first cut borrowed the carve's wide
    // 0.35 band and the fade leaked into the spiral regime: a half-strength
    // governor loses the blend to the saturated pursuit and the Spitfire
    // was back in the dirt (altLost 505 m, bank 101, the exact report this
    // governor retired).
    const fwdY = FDM.qRotate(state.att, FDM.v3(1, 0, 0)).y;
    const vertical = clamp((0.95 - Math.abs(fwdY)) / 0.15, 0, 1);
    const w = this.govW * high * upright * vertical;
    return roll * (1 - w) + capture * w;
  }
}

/**
 * The predictive steering chain, whole: the shipped pursuit/carve flies the
 * ROLL (with the carve's endgame band widened — steerTowardAssist reads
 * limits.predictive), the solver REPLACES pitch and rudder, and the bank
 * governor has the last word on the wings. One entry point so the client
 * (AimDirector.steer) and the feel probes drive the exact same chain —
 * tuning measured there is tuning shipped, as always.
 */
export function steerPredictive(inst, cfg, state, dir, limits, dt = 1 / 60) {
  const a = FDM.steerTowardAssist(state, dir, limits);
  const pr = inst.solve(cfg, state, dir, limits, a.roll, limits.throttle ?? 0.7);
  a.pitch = pr.pitch;
  a.yaw = pr.yaw;
  a.roll = clamp(inst.governRoll(cfg, state, dir, limits, a.roll, dt), -1, 1);
  return a;
}
